import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import '../../style/tracking.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icons for livreur and destination
const livreurIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const TrackingPage = () => {
  const { token } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]); // Default: Paris

  useEffect(() => {
    fetchTrackingData();

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchTrackingData();
    }, 10000);

    return () => clearInterval(interval);
  }, [token]);

  const fetchTrackingData = async () => {
    try {
      const response = await axios.get(`/api/track/${token}`);

      if (response.data.success) {
        setTrackingData(response.data);
        setError(null);

        // Center map on livreur position if available
        if (response.data.delivery?.current_position) {
          const { latitude, longitude } = response.data.delivery.current_position;
          setMapCenter([parseFloat(latitude), parseFloat(longitude)]);
        }
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement du suivi');
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      0: 'En attente',
      1: 'En cours de livraison',
      2: 'Livrée',
      3: 'Annulée'
    };
    return statusLabels[status] || 'Statut inconnu';
  };

  const getStatusColor = (status) => {
    const colors = {
      0: '#FFA500',
      1: '#0066CC',
      2: '#28A745',
      3: '#DC3545'
    };
    return colors[status] || '#6C757D';
  };

  if (loading) {
    return (
      <div className="tracking-container">
        <div className="tracking-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
          <p>Chargement des informations de livraison...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tracking-container">
        <div className="tracking-error">
          <h2>Erreur</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!trackingData) {
    return null;
  }

  const { order, delivery } = trackingData;
  const hasPosition = delivery?.current_position && delivery?.destination;

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h1>Suivi de votre livraison</h1>
        <div className="tracking-order-id">Commande #{order.id}</div>
      </div>

      <div className="tracking-content">
        {/* Status Card */}
        <div className="tracking-card">
          <div className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
            {getStatusLabel(order.status)}
          </div>

          <div className="tracking-info">
            <div className="info-row">
              <span className="info-label">Fournisseur:</span>
              <span className="info-value">{order.fournisseur_name}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Adresse de livraison:</span>
              <span className="info-value">{order.delivery_address}</span>
            </div>

            {order.delivery_date && (
              <div className="info-row">
                <span className="info-label">Date de livraison:</span>
                <span className="info-value">{new Date(order.delivery_date).toLocaleDateString('fr-FR')}</span>
              </div>
            )}

            {delivery?.livreur && (
              <div className="info-row">
                <span className="info-label">Livreur:</span>
                <span className="info-value">
                  {delivery.livreur.name}
                  {delivery.livreur.is_online && (
                    <span className="online-badge">En ligne</span>
                  )}
                </span>
              </div>
            )}

            {delivery?.eta && (
              <div className="info-row">
                <span className="info-label">Temps estimé d'arrivée:</span>
                <span className="info-value eta">{delivery.eta}</span>
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        {hasPosition ? (
          <div className="tracking-map-container">
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: '500px', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Livreur position */}
              {delivery.current_position && (
                <Marker
                  position={[
                    parseFloat(delivery.current_position.latitude),
                    parseFloat(delivery.current_position.longitude)
                  ]}
                  icon={livreurIcon}
                >
                  <Popup>
                    <strong>Livreur</strong><br />
                    {delivery.livreur?.name || 'En cours de livraison'}
                  </Popup>
                </Marker>
              )}

              {/* Destination */}
              {delivery.destination && (
                <Marker
                  position={[
                    parseFloat(delivery.destination.latitude),
                    parseFloat(delivery.destination.longitude)
                  ]}
                  icon={destinationIcon}
                >
                  <Popup>
                    <strong>Destination</strong><br />
                    {order.delivery_address}
                  </Popup>
                </Marker>
              )}

              {/* Line between livreur and destination */}
              {delivery.current_position && delivery.destination && (
                <Polyline
                  positions={[
                    [
                      parseFloat(delivery.current_position.latitude),
                      parseFloat(delivery.current_position.longitude)
                    ],
                    [
                      parseFloat(delivery.destination.latitude),
                      parseFloat(delivery.destination.longitude)
                    ]
                  ]}
                  color="#0066CC"
                  weight={3}
                  opacity={0.6}
                  dashArray="10, 10"
                />
              )}
            </MapContainer>

            <div className="map-legend">
              <div className="legend-item">
                <span className="legend-marker blue"></span>
                <span>Livreur</span>
              </div>
              <div className="legend-item">
                <span className="legend-marker red"></span>
                <span>Destination</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="tracking-no-map">
            <p>La position du livreur n'est pas encore disponible.</p>
          </div>
        )}

        {/* Auto-refresh indicator */}
        <div className="tracking-footer">
          <span className="refresh-indicator">
            Mise à jour automatique toutes les 10 secondes
          </span>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
